import React from 'react'
import PropTypes from 'prop-types'
import { InputText } from 'primereact/inputtext'
import './WingsTextInput.css'

/**
 * WingsTextInput Component
 *
 * A single-line text input built on PrimeReact's InputText, styled to match
 * the Wingspans design system. Supports optional label, helper text, and
 * error state.
 *
 * @component
 * @example
 * // Basic usage
 * <WingsTextInput
 *   label="First Name"
 *   placeholder="Enter your first name"
 *   value={value}
 *   onChange={(e) => setValue(e.target.value)}
 * />
 *
 * @example
 * // With error state
 * <WingsTextInput
 *   label="Email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   error="Please enter a valid email address"
 * />
 *
 * @example
 * // With helper text
 * <WingsTextInput
 *   label="Username"
 *   helpText="Must be at least 3 characters"
 *   value={username}
 *   onChange={(e) => setUsername(e.target.value)}
 * />
 */
const WingsTextInput = ({
  label,
  helpText,
  error,
  id,
  size = 'medium',
  fullWidth = false,
  disabled = false,
  className = '',
  ...props
}) => {
  const inputId = id || (label ? label.toLowerCase().replace(/\s+/g, '-') : undefined)

  const wrapperClasses = [
    'wings-text-input-wrapper',
    fullWidth && 'wings-text-input-wrapper--full',
    error && 'wings-text-input-wrapper--error',
    className,
  ].filter(Boolean).join(' ')

  const inputClasses = [
    'wings-text-input',
    `wings-text-input--${size}`,
    error && 'wings-text-input--error',
    disabled && 'wings-text-input--disabled',
  ].filter(Boolean).join(' ')

  return (
    <div className={wrapperClasses}>
      {label && (
        <label className="wings-text-input-label" htmlFor={inputId}>
          {label}
        </label>
      )}
      <InputText
        id={inputId}
        className={inputClasses}
        disabled={disabled}
        aria-describedby={helpText || error ? `${inputId}-hint` : undefined}
        aria-invalid={!!error}
        {...props}
      />
      {(helpText || error) && (
        <span
          id={`${inputId}-hint`}
          className={`wings-text-input-hint${error ? ' wings-text-input-hint--error' : ''}`}
        >
          {error || helpText}
        </span>
      )}
    </div>
  )
}

WingsTextInput.propTypes = {
  /** Label displayed above the input */
  label: PropTypes.string,
  /** Helper text displayed below the input */
  helpText: PropTypes.string,
  /** Error message — puts the input in an error state and replaces helpText */
  error: PropTypes.string,
  /** HTML id for the input; auto-generated from label if omitted */
  id: PropTypes.string,
  /** Size variant controlling font size and padding */
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  /** Expands the input to fill its container width */
  fullWidth: PropTypes.bool,
  /** Disables the input */
  disabled: PropTypes.bool,
  /** Additional CSS classes applied to the wrapper element */
  className: PropTypes.string,
  /** Current value of the input */
  value: PropTypes.string,
  /** Change handler — receives the native event */
  onChange: PropTypes.func,
  /** Placeholder text */
  placeholder: PropTypes.string,
}

export default WingsTextInput
